// react
import { useDispatch } from 'react-redux';
import { useCallback } from 'react';

// utils
import { reportInviteError } from 'utils/inviteError';

// components
import EnterprisePortalPage from '@karehero/llama/components/pages/EnterprisePortal/EnterprisePortal';
import { useParams } from 'react-router-dom';

// hooks
import { useGoBack } from 'hooks/useGoBack';

// store
import { useSelector } from 'store';
import {
  useGetOrganizationAccountsQuery,
  useGetOrganizationQuery,
  useInviteMembersToOrganizationAdminMutation,
  useAddInviteKeysToOrganizationAdminMutation,
  useReinviteMemberMutation,
  useResendEmailInvitesToAllMembersMutation,
} from 'store/api/organization';
import { newToast } from 'store/slices/uiSlice';

/**
 * OrganizationMemberTable shows the accounts and invited members of an organization
 */
export const OrganizationMemberTable = () => {
  // params
  const { organizationID } = useParams();

  // hooks
  const goBack = useGoBack();
  const [reinviteMember] = useReinviteMemberMutation();
  const [resendEmailInvitesToAllMembers] =
    useResendEmailInvitesToAllMembersMutation();
  const [inviteMembers] = useInviteMembersToOrganizationAdminMutation();
  const [addInviteKeys] = useAddInviteKeysToOrganizationAdminMutation();
  const dispatch = useDispatch();
  const { data: organization } = useGetOrganizationQuery(organizationID || '');
  const { data: accounts } = useGetOrganizationAccountsQuery(
    { organizationID: organizationID || '' },
    { skip: !organizationID },
  );
  const account = useSelector((state) => state.iam.account);

  const isAdmin = account?.accountRole.name === 'KareHero Administrator';

  // methods
  const handleResendInvite = useCallback(
    async (id?: string, email?: string) => {
      if (!id) return;
      try {
        await reinviteMember(id).unwrap();
        dispatch(
          newToast({
            title: 'Invitation Success',
            description: `Resent invitation to ${email || 'the user'}`,
          }),
        );
      } catch (e) {
        reportInviteError(e);
      }
    },
    [dispatch, reinviteMember],
  );

  const handleResendAllEmail = useCallback(async () => {
    const res = await resendEmailInvitesToAllMembers(organization?.id || '');

    if ('error' in res) {
      console.error('Error resending email invites:', res.error);
      dispatch(
        newToast({
          title: 'Error',
          description: 'Failed to resend email invites.',
        }),
      );
      return;
    }

    dispatch(
      newToast({
        title: 'Success',
        description: 'Email invites resent successfully.',
      }),
    );
  }, [resendEmailInvitesToAllMembers, organization?.id, dispatch]);

  return (
    <EnterprisePortalPage
      accounts={accounts}
      resendInvite={handleResendInvite}
      resendEmailInvites={handleResendAllEmail}
      organization={organization}
      inviteMembers={(req: any) =>
        organizationID && inviteMembers({ ...req, organizationID })
      }
      addInviteKeys={(req: any) =>
        organizationID && addInviteKeys({ ...req, organizationID })
      }
      notifyToast={(req: { title: string; description: string }) =>
        dispatch(newToast(req))
      }
      isAdmin={isAdmin}
      onBack={goBack}
    />
  );
};

export default OrganizationMemberTable;
